
import React, { useState, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';
import { supabase } from '@/integrations/supabase/client';
import ExpenseForm from '@/components/expenses/ExpenseForm';
import ExpenseTable from '@/components/expenses/ExpenseTable';
import SettleUpDashboard from '@/components/expenses/SettleUpDashboard';
import LoadingSpinner from '@/components/itinerary/LoadingSpinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Expense } from '@/types/expense';
import { Trip } from '@/types/itinerary';

interface TripContextType {
  trip: Trip;
  isAuthorized: boolean;
}

const Expenses: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const { tripId: tripIdFromParams } = useParams<{ tripId: string }>();
  const context = useOutletContext<TripContextType | null>();
  
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Use trip ID from context if available, otherwise fallback to URL params or query params
  const activeTrip = context?.trip?.id || tripIdFromParams;
  
  // Check if we're in trip layout
  const inTripLayout = !!context;

  // Listen for payment info changes in localStorage
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key?.startsWith('payment_info_')) {
        // Force a re-render when payment info changes
        console.log('Payment info updated:', event.key);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Fetch expenses for the active trip
  useEffect(() => {
    const fetchExpenses = async () => {
      if (!activeTrip) return;
      
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('expenses')
          .select('*')
          .eq('trip_id', activeTrip)
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        setExpenses(data || []);
      } catch (error) {
        console.error('Error fetching expenses:', error);
        toast.error('Failed to load expenses');
      } finally {
        setIsLoading(false);
      }
    };

    fetchExpenses();
    
    // Subscribe to realtime updates
    const channel = supabase
      .channel('expenses-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'expenses',
        filter: `trip_id=eq.${activeTrip}`,
      }, (payload) => {
        console.log('Realtime update:', payload);
        fetchExpenses();
      })
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeTrip]);

  // For demo purposes, create a trip if none is available
  useEffect(() => {
    const getTripId = async () => {
      if (activeTrip) return; // Skip if we already have a trip ID
      
      try {
        const { data, error } = await supabase
          .from('trips')
          .select('id')
          .limit(1)
          .single();
          
        if (error) {
          // If no trips exist, we'll create a demo trip
          const { data: newTrip, error: createError } = await supabase
            .from('trips')
            .insert({
              name: 'Demo Trip',
              description: 'A demo trip for expenses',
              code: 'DEMO' + Math.floor(1000 + Math.random() * 9000),
              owner_id: user?.id,
            })
            .select('id')
            .single();
            
          if (createError) throw createError;
          // No need to set active trip here as we're using the context
        }
      } catch (error) {
        console.error('Error getting trip ID:', error);
        toast.error('Failed to load trip');
      }
    };

    if (user && !activeTrip && !inTripLayout) {
      getTripId();
    }
  }, [user, activeTrip, inTripLayout]);

  if (authLoading || (!activeTrip && !inTripLayout)) {
    return <LoadingSpinner />;
  }

  return (
    <div className={inTripLayout ? "" : "container mx-auto px-4 pt-24 pb-10"}>
      <h2 className={`text-2xl font-bold ${inTripLayout ? "text-[#0A3D62]" : "text-navteal"} font-poppins mb-4`}>
        Trip Expenses
      </h2>
      
      <Tabs defaultValue="expenses" className="w-full">
        <TabsList className="bg-muted/50 mb-6">
          <TabsTrigger value="expenses" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Expenses</TabsTrigger>
          <TabsTrigger value="settle" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Settle Up</TabsTrigger>
        </TabsList>
        
        <TabsContent value="expenses" className="space-y-6">
          {user ? (
            <ExpenseForm tripId={activeTrip || ""} userId={user.id} />
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6">
              <p className="text-amber-700">
                Sign in to add and edit expenses.
              </p>
            </div>
          )}
          
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <ExpenseTable expenses={expenses} currentUserId={user?.id} />
          )}
        </TabsContent>
        
        <TabsContent value="settle">
          <SettleUpDashboard tripId={activeTrip || ""} expenses={expenses} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Expenses;
